/**
 * Admin-only: aggregated anonymous feedback themes from Slack /ama.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  api,
  type FeedbackThemeRow,
  type FeedbackThemeTone,
  type FeedbackThemesResponse,
} from '../lib/api'
import {
  ArrowDown,
  ArrowUp,
  Download,
  Info,
  Loader2,
  MessageSquare,
  Minus,
  Sparkles,
} from 'lucide-react'

const WARM_CARD = '#FDFCF8'
const WARM_BORDER = '#e0d9cc'
const BRAND_TEAL = '#2A7A8A'

/** Matches worker `feedbackToneFromAvg` when API omits tone (older responses). */
function toneFromAvg(avg: number | null | undefined): FeedbackThemeTone | null {
  if (avg == null || Number.isNaN(avg)) return null
  if (avg <= 0.35) return 'positive'
  if (avg >= 0.65) return 'negative'
  return 'mixed'
}

function tone7dForRow(row: FeedbackThemeRow): FeedbackThemeTone | null {
  return row.tone7d ?? toneFromAvg(row.avgSentiment7d)
}

function toneBadge(t: FeedbackThemeTone | null) {
  if (t === 'positive') {
    return (
      <span
        className="inline-flex text-[10px] font-semibold px-2 py-0.5 rounded-full border"
        style={{ background: '#eef5ec', color: '#1a4d2e', borderColor: '#B8C7B1' }}
      >
        Positive-leaning
      </span>
    )
  }
  if (t === 'negative') {
    return (
      <span
        className="inline-flex text-[10px] font-semibold px-2 py-0.5 rounded-full border"
        style={{ background: '#fdf2f2', color: '#7f1d1d', borderColor: '#e8cfcf' }}
      >
        Negative-leaning
      </span>
    )
  }
  if (t === 'mixed') {
    return (
      <span
        className="inline-flex text-[10px] font-semibold px-2 py-0.5 rounded-full border"
        style={{ background: '#fdf8f0', color: '#713f12', borderColor: '#e8d4b8' }}
      >
        Mixed
      </span>
    )
  }
  return <span className="text-xs text-ink-faint">—</span>
}

function trendBadge(t: FeedbackThemeRow['trend']) {
  if (t === 'new') {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full"
        style={{ background: '#eef6fb', color: BRAND_TEAL, border: '1px solid #a7d8e0' }}
      >
        <Sparkles size={10} /> New
      </span>
    )
  }
  if (t === 'up') {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-medium text-emerald-700">
        <ArrowUp size={14} /> Up vs prior week
      </span>
    )
  }
  if (t === 'down') {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-medium text-amber-800">
        <ArrowDown size={14} /> Down vs prior week
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-xs text-ink-soft">
      <Minus size={14} /> Flat
    </span>
  )
}

const TH_TOOLTIP_7D =
  'Number of anonymous /ama submissions grouped under this theme in the last 7 days.'
const TH_TOOLTIP_30D =
  'Number of anonymous /ama submissions grouped under this theme in the last 30 days.'
const TH_TOOLTIP_NEG =
  'Average model score from 0 to 1: higher means submissions in the last 7 days used more negative wording (DistilBERT). Not a judgment of whether the feedback is fair or accurate.'
const TH_TOOLTIP_THEME = 'Short 2–4 word label for scanning (grouped for counts).'
const TH_TOOLTIP_DETAILS =
  'Optional context from the AI for leaders who want more than the headline. Older rows may be parsed from a single stored field.'

function csvEscape(s: string) {
  return `"${String(s).replace(/"/g, '""')}"`
}

function downloadCsv(data: FeedbackThemesResponse) {
  const header =
    'theme,theme_details,count_7d,count_30d,count_prior_7d,avg_sentiment_7d,avg_sentiment_30d,tone_7d,tone_30d,trend'
  const lines = data.themes.map((row) =>
    [
      csvEscape(row.theme),
      row.themeDetails != null && row.themeDetails.trim() !== '' ? csvEscape(row.themeDetails) : '""',
      row.count7d,
      row.count30d,
      row.countPrev7d,
      row.avgSentiment7d ?? '',
      row.avgSentiment30d ?? '',
      row.tone7d ?? '',
      row.tone30d ?? '',
      row.trend,
    ].join(',')
  )
  const meta = [
    '',
    `"totals_7d",${data.totalSubmissions7d},,,,,,,,`,
    `"totals_30d",,${data.totalSubmissions30d},,,,,,,`,
  ]
  const blob = new Blob([[header, ...lines, ...meta].join('\n')], {
    type: 'text/csv;charset=utf-8',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `duva-feedback-themes-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

export function FeedbackThemesPage() {
  const [data, setData] = useState<FeedbackThemesResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    api
      .getFeedbackThemes()
      .then((res) => setData(res as FeedbackThemesResponse))
      .catch((e) =>
        setError(
          e instanceof Error
            ? e.message
            : 'Could not load feedback themes. If you are an org admin, try refreshing; otherwise ask an admin to grant access.'
        )
      )
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div
        className="mb-6 rounded-xl border px-4 py-3 text-sm flex gap-3"
        style={{ background: WARM_CARD, borderColor: WARM_BORDER, color: '#3d6068' }}
      >
        <Info size={18} className="shrink-0 mt-0.5 text-brand-teal" />
        <div className="space-y-2">
          <p>
            <span className="font-medium text-ink">Anonymous mirror.</span> This table shows{' '}
            <strong>AI-generated theme labels</strong> from Slack <code className="text-xs bg-warm-inset px-1 rounded">/ama</code>{' '}
            submissions. <strong>Raw messages are not stored</strong> in Duva. Themes are grouped when they
            differ only by spacing or case. Use this for leadership listening and all-hands themes—not for
            investigating individuals.
          </p>
          <p className="text-xs text-ink-soft">
            When the same theme appears <strong>3+ times in 7 days</strong>, admins may also get a Slack
            &quot;Theme alert&quot; (if Slack is connected).
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-7">
        <div>
          <h1
            className="text-2xl font-bold text-ink flex items-center gap-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            <MessageSquare size={26} className="text-brand-teal shrink-0" />
            Anonymous feedback themes
          </h1>
          <p className="text-sm mt-1" style={{ color: '#6b8a8f' }}>
            Rolling 30-day window · trends compare last 7 days vs the prior 7 days
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="text-xs font-medium px-3 py-2 rounded-lg border border-warm-border bg-warm-card text-ink hover:bg-warm-inset disabled:opacity-50"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={() => data && downloadCsv(data)}
            disabled={!data || data.themes.length === 0}
            className="flex items-center gap-2 text-xs font-medium px-4 py-2 rounded-full text-white disabled:opacity-50 shadow-glow-teal"
            style={{ background: BRAND_TEAL }}
          >
            <Download size={14} />
            Download CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 gap-2 text-ink-muted">
          <Loader2 size={22} className="animate-spin text-brand-teal" />
          Loading themes…
        </div>
      ) : data && data.themes.length === 0 ? (
        <div
          className="rounded-2xl border p-10 text-center shadow-card"
          style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
        >
          <p className="text-sm font-medium text-ink">No themes in the last 30 days</p>
          <p className="text-xs text-ink-muted mt-2 max-w-md mx-auto">
            When teammates use <code className="text-[11px] bg-warm-inset px-1 rounded">/ama</code> in Slack,
            summarized themes will appear here. Totals below count all submissions in each window (including
            rows still being clustered).
          </p>
          <p className="text-xs text-ink-soft mt-4">
            Last 7 days: <strong>{data.totalSubmissions7d}</strong> · Last 30 days:{' '}
            <strong>{data.totalSubmissions30d}</strong>
          </p>
        </div>
      ) : data ? (
        <>
          <div
            className="mb-5 rounded-xl border px-4 py-3 text-xs space-y-2"
            style={{ background: '#f9f7f2', borderColor: WARM_BORDER, color: '#3d6068' }}
          >
            <p className="font-semibold text-ink">How to read this table</p>
            <ul className="list-disc pl-4 space-y-1 text-ink-muted leading-relaxed">
              <li>
                <strong className="text-ink">Theme:</strong> short label to scan the main issue.{' '}
                <strong className="text-ink">Theme details:</strong> longer explanation when the model (or legacy parsing)
                provides it—read when you want depth.
              </li>
              <li>
                <strong className="text-ink">7d / 30d:</strong> how many anonymous <code className="text-[10px] bg-warm-inset px-1 rounded">/ama</code>{' '}
                submissions were grouped under this theme in the last 7 or 30 days.
              </li>
              <li>
                <strong className="text-ink">Avg. negativity (7d):</strong> average model score from{' '}
                <strong>0 to 1</strong>. Higher means the wording in those submissions sounded{' '}
                <strong>more negative</strong> to the classifier (DistilBERT SST-2)—not whether the concern is valid.
              </li>
              <li>
                <strong className="text-ink">Tone:</strong> bands from that score—positive-leaning (≤0.35), mixed
                (0.35–0.65), negative-leaning (≥0.65).
              </li>
              <li>
                <strong className="text-ink">Trend:</strong> compares submission count in the last 7 days to the{' '}
                <em>prior</em> 7 days (not shown as a column).
              </li>
            </ul>
          </div>

          <div className="flex flex-wrap gap-4 mb-6 text-sm">
            <div
              className="rounded-xl border px-4 py-3 shadow-card"
              style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
              title="All /ama submissions in the org in the last 7 days (every theme row sums into this total)."
            >
              <p className="text-[10px] uppercase tracking-wide text-ink-soft font-semibold">
                Submissions (7d)
              </p>
              <p className="text-2xl font-bold text-ink tabular-nums">{data.totalSubmissions7d}</p>
            </div>
            <div
              className="rounded-xl border px-4 py-3 shadow-card"
              style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
              title="All /ama submissions in the org in the last 30 days."
            >
              <p className="text-[10px] uppercase tracking-wide text-ink-soft font-semibold">
                Submissions (30d)
              </p>
              <p className="text-2xl font-bold text-ink tabular-nums">{data.totalSubmissions30d}</p>
            </div>
            <div
              className="rounded-xl border px-4 py-3 shadow-card min-w-[200px]"
              style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
              title="Number of distinct theme labels in the table (30-day window)."
            >
              <p className="text-[10px] uppercase tracking-wide text-ink-soft font-semibold">
                Distinct themes (30d)
              </p>
              <p className="text-2xl font-bold text-ink tabular-nums">{data.themes.length}</p>
            </div>
          </div>

          <div
            className="rounded-2xl border overflow-hidden shadow-card"
            style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: `1px solid ${WARM_BORDER}`, background: '#f9f7f2' }}>
                    <th
                      className="text-left font-semibold text-ink-soft px-4 py-3 w-36 min-w-[8rem] cursor-help"
                      title={TH_TOOLTIP_THEME}
                    >
                      Theme
                    </th>
                    <th
                      className="text-left font-semibold text-ink-soft px-4 py-3 min-w-[14rem] max-w-md cursor-help"
                      title={TH_TOOLTIP_DETAILS}
                    >
                      Theme details
                    </th>
                    <th
                      className="text-right font-semibold text-ink-soft px-2 py-3 w-20 cursor-help"
                      title={TH_TOOLTIP_7D}
                    >
                      7d
                    </th>
                    <th
                      className="text-right font-semibold text-ink-soft px-2 py-3 w-20 cursor-help"
                      title={TH_TOOLTIP_30D}
                    >
                      30d
                    </th>
                    <th
                      className="text-right font-semibold text-ink-soft px-2 py-3 min-w-[140px] hidden md:table-cell cursor-help"
                      title={TH_TOOLTIP_NEG}
                    >
                      Avg. negativity (7d)
                    </th>
                    <th className="text-left font-semibold text-ink-soft px-4 py-3 min-w-[140px]">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {data.themes.map((row) => {
                    const t7 = tone7dForRow(row)
                    return (
                      <tr
                        key={row.normalizedKey}
                        style={{ borderBottom: `1px solid ${WARM_BORDER}` }}
                        className="hover:bg-warm-inset/50"
                      >
                        <td className="px-4 py-3 text-ink align-top">
                          <div className="font-medium text-ink">{row.theme}</div>
                          <div className="md:hidden mt-2 flex flex-wrap items-center gap-2">
                            {toneBadge(t7)}
                            {row.avgSentiment7d != null ? (
                              <span className="text-[11px] text-ink-muted tabular-nums">
                                score {row.avgSentiment7d.toFixed(3)}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-top text-ink-muted text-xs leading-relaxed max-w-lg">
                          {row.themeDetails != null && row.themeDetails.trim() !== '' ? (
                            <p className="whitespace-pre-wrap">{row.themeDetails}</p>
                          ) : (
                            <span className="text-ink-faint">—</span>
                          )}
                        </td>
                        <td className="px-2 py-3 text-right tabular-nums text-ink" title={TH_TOOLTIP_7D}>
                          {row.count7d}
                        </td>
                        <td className="px-2 py-3 text-right tabular-nums text-ink-muted" title={TH_TOOLTIP_30D}>
                          {row.count30d}
                        </td>
                        <td className="px-2 py-3 text-right hidden md:table-cell">
                          <div className="flex flex-col items-end gap-1.5">
                            {toneBadge(t7)}
                            <span className="tabular-nums text-ink-muted text-xs" title={TH_TOOLTIP_NEG}>
                              {row.avgSentiment7d != null ? row.avgSentiment7d.toFixed(3) : '—'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">{trendBadge(row.trend)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <p className="mt-4 text-[11px] text-ink-faint leading-relaxed">{data.note}</p>
        </>
      ) : null}
    </div>
  )
}
